import { Column, Container, Header, Layout } from '@/components/SettingsLayout';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { debounce } from '@/utils/helpers/debounce';
import SkeletonTable from '@/components/SkeletonLoader/SkeletonTable';
import { useGetUser } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { CompanyTypes } from '@/types';
import OrganizationsFilters from './OrganizationsFilters';
import {
  CompaniesCount,
  OrgFilter,
  SortByOrgFilter,
  SortOrder,
  useGetAllOrganizationQuery,
} from '@/store/slices/organizationsSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import OrganizationsTable from './OrganizationTable';
import { Text } from '@/ui';
import { Pagination } from '@/components';
import { usePaginationFilter } from '@/hooks/usePaginationFilter';

export const accountsRef = '/cdk/organizations';
function Accounts() {
  const { t } = useTranslation();
  const [, setSearchQuery] = useState('');
  const { currentUser, company } = useGetUser();
  const navigate = useNavigate();
  const { filter, setFilter, page, setPage, perPage, setPerPage } = usePaginationFilter<OrgFilter>({
    search: '',
    limit: 25,
    offset: 0,
    type: CompanyTypes.ORGANIZATION,
    organizationId: company?._id,
    sortBy: SortByOrgFilter.NO_OF_COMPANIES,
    sortOrder: SortOrder.DESC,
    companiesCount: CompaniesCount.ALL,
  });
  const { data, isLoading, isError } = useGetAllOrganizationQuery(
    company?._id
      ? {
          id: company._id,
          filter,
        }
      : skipToken,
  );

  useEffect(() => {
    if (company?.type !== CompanyTypes.ROOT) {
      navigate(`/cdk/organizations/${company?._id}`);
    }
  }, [currentUser, navigate]);

  const saveInput = useCallback((input: string) => {
    const lowercasedInput = input.toLowerCase();
    setSearchQuery(lowercasedInput);
  }, []);

  const debouncedSaveInput = debounce(saveInput, 500);

  const handleChange = (name: string, value: any) => {
    setFilter((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  if (isLoading)
    return (
      <Layout>
        <Container size='lg'>
          <Header>
            <h1>{t('Accounts.title')}</h1>
          </Header>
          <SkeletonTable />
        </Container>
      </Layout>
    );

  if (isError) {
    const message = t('Accounts.error');
    throw new Error(message);
  }

  return (
    <Layout>
      <Container size='lg'>
        <Header>
          <h1>{t('Accounts.organization_list')}</h1>
        </Header>
        <Column gapSize='16'>
          <OrganizationsFilters
            debouncedSaveInput={debouncedSaveInput}
            handleChange={handleChange}
          />
          {data?.data && data?.data?.length > 0 ? (
            <OrganizationsTable organizations={data?.data} />
          ) : (
            <Text size='s' weight='light'>
              {t('Companies.no_organizations_yet')}
            </Text>
          )}
          {data?.count !== undefined && data?.count > 0 && data?.count >= filter?.limit && (
            <Pagination
              page={page}
              totalPages={Math.ceil(data?.count / Number(perPage))}
              setPage={setPage}
              perPage={String(perPage)}
              setPerPage={setPerPage}
            />
          )}
        </Column>
      </Container>
    </Layout>
  );
}

export default Accounts;
